import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ExternalLink, Shield, Link as LinkIcon, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { retryScrutinyBlockchain } from "../../lib/api";
import { toast } from "sonner";

export function BlockchainTab({ results, onRefresh }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [retrying, setRetrying] = useState(false);

  return (
    <div className="space-y-6" data-testid="blockchain-tab">
      {/* Vote coverage stats */}
      {results.blockchain_votes && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className={`p-2 rounded-lg ${results.blockchain_votes.complete ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              {results.blockchain_votes.complete
                ? <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                : <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
              }
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{t('blockchain.voteCoverage', 'Vots a la blockchain')}</h3>
              <p className="text-sm text-slate-500">
                {results.blockchain_votes.complete
                  ? t('blockchain.allVotesOnChain', 'Tots els vots estan registrats a la blockchain.')
                  : t('blockchain.votesBeingWritten', "Els vots s'estan escrivint a la blockchain. Aquesta pagina s'actualitzara automaticament.")
                }
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-900">{results.blockchain_votes.total}</div>
              <div className="text-xs text-slate-500">{t('blockchain.totalVotes', 'Total vots')}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{results.blockchain_votes.on_chain}</div>
              <div className="text-xs text-slate-500">{t('blockchain.onChain', 'On-chain')}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-500">{results.blockchain_votes.pending}</div>
              <div className="text-xs text-slate-500">{t('blockchain.pending', 'Pendents')}</div>
            </div>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${results.blockchain_votes.complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${results.blockchain_votes.percentage}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 text-right">{results.blockchain_votes.percentage}%</p>
        </motion.div>
      )}

      {/* Scrutiny TX */}
      {results.solana_scrutiny_tx ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-emerald-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-emerald-100 rounded-lg"><LinkIcon className="h-5 w-5 text-emerald-700" /></div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{t('blockchain.scrutinyResult', 'Resultat escrutini a la blockchain')}</h3>
              <p className="text-sm text-slate-500">{t('blockchain.scrutinyRecorded', "Els resultats d'aquest escrutini estan registrats a la blockchain de Solana.")}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1 font-medium">{t('blockchain.txSignature', 'Signatura de la transaccio')}</p>
              <p className="font-mono text-sm text-slate-900 break-all" data-testid="scrutiny-tx-hash">{results.solana_scrutiny_tx}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1 font-medium">{t('blockchain.network', 'Xarxa')}</p>
              <p className="text-sm text-slate-900">Solana Mainnet</p>
            </div>
            <a href={`https://solscan.io/tx/${results.solana_scrutiny_tx}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
              data-testid="scrutiny-solscan-link">
              <ExternalLink className="h-4 w-4" />
              {t('blockchain.viewOnSolscan', 'Verificar a Solscan')}
            </a>
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-amber-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><Loader2 className="h-5 w-5 text-amber-600 animate-spin" /></div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('blockchain.scrutinyResult', 'Resultat escrutini a la blockchain')}</h3>
                <p className="text-sm text-amber-600 font-medium">{t('blockchain.scrutinyPending', 'Pendent de validacio a la cadena. Esperant el hash de la transaccio...')}</p>
              </div>
            </div>
            <button
              data-testid="retry-blockchain-btn"
              onClick={async () => {
                setRetrying(true);
                try {
                  const res = await retryScrutinyBlockchain(id);
                  toast.success(res.data.message || t('blockchain.retryStarted', 'Re-intent iniciat'));
                  setTimeout(() => { if (onRefresh) onRefresh(); }, 5000);
                } catch (e) {
                  toast.error(e.response?.data?.detail || t('errors.generic'));
                } finally {
                  setRetrying(false);
                }
              }}
              disabled={retrying}
              className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? t('blockchain.retrying', 'Reintentant...') : t('blockchain.retry', 'Reintentar')}
            </button>
          </div>
        </motion.div>
      )}

      {/* Explanation */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800 mb-1">{t('blockchain.whatIsThis', "Que es el registre blockchain?")}</p>
            <p className="text-xs text-emerald-700 leading-relaxed">
              {t('blockchain.explanation', "Quan s'escrutina una consulta, els resultats es registren a la blockchain de Solana com una transaccio immutable. Aixo inclou el nom de l'entitat, el nom de la consulta, les dates i els resultats de cada proposta. Qualsevol persona pot verificar aquesta informacio a Solscan.")}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
